import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { GreenStandby } from "@/components/GreenStandby";
import { NotificationsBell } from "@/components/NotificationsBell";
import { ImpersonationGuard } from "@/components/ImpersonationGuard";
import { ServiceHistoryPage } from "@/components/ServiceHistoryButton";
import { DealerBalanceBar } from "@/components/DealerBalanceBar";
import { useOperators } from "@/components/mobile/operators";
import { UplineLine } from "@/components/UplineLine";
import { DisplayControls } from "@/components/DisplayControls";
import { CompanyIdentity } from "@/components/CompanyIdentity";
import { ComplianceOrderPrompt } from "@/components/ComplianceOrderPrompt";
import { WorkspaceNavigation } from "@/components/WorkspaceNavigation";
import { InstallAppBanner, InstallAppButton } from "@/components/PwaInstall";
import { Sheet, SheetTrigger, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Menu as MobileMenuIcon, Power, Settings, ChevronDown } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { GlobalSearch } from "@/components/GlobalSearch";
import { AccountStatusBanners } from "@/components/AccountStatusBanners";
import { MasterNetworkMenu } from "@/components/MasterNetworkMenu";
import {
  LayoutDashboard, ShoppingBag, Smartphone, Zap, Users, FileText, Store, Package,
  LogOut, LogIn, Signal, Receipt, BarChart3, Tags, Gift, Plug, ShoppingCart, BadgePercent,
  RefreshCw, Search, PhoneCall, ReceiptText, Flame, Home, Boxes, Wallet, UserCog, HandCoins, Coins, Trash2, Mail, Truck, Undo2, Sparkles, CreditCard, PackageCheck, BookUser, LifeBuoy, Network, AlertTriangle, Palette, BookOpen, Briefcase, Plus, Minus, Wrench, History, Wifi, ShieldCheck,
} from "lucide-react";

const NAV = {
  admin: [
    { label: null, items: [
      { to: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    ] },
    { label: "Rete", items: [
      { to: "dealers?tab=master", label: "Master Dealer", icon: Network },
      { to: "dealers", label: "Dealer", icon: Store },
      { to: "utilizzo-dealer", label: "Utilizzo dealer", icon: BarChart3 },
      { to: "dealers?tab=customers", label: "Clienti finali", icon: Users },
      { to: "agenzie", label: "Agenzie", icon: Briefcase },
      { to: "utenti", label: "Utenti interni", icon: UserCog },
    ] },
    { label: "Vendite & Catalogo", items: [
      { to: "prodotti", label: "Catalogo Prodotti", icon: Package },
      { to: "categorie", label: "Categorie prodotti", icon: Tags },
      { to: "negozio-hardware", label: "Ordini hardware", icon: Receipt },
      { to: "kit-accessori", label: "Kit accessori", icon: Package },
    ] },
    { label: "Luce & Gas", items: [
      { to: "richieste-energia", label: "Pratiche luce/gas", icon: Flame },
      { to: "config-energia", label: "Impostazioni Venergia", icon: Plug },
      { to: "provvigioni-offerte-energia", label: "Listino", icon: Coins },
      { to: "offerte-rete", label: "Offerte energia attive", icon: Package },
    ] },
    { label: "Mobile", items: [
      { to: "operatori-mobili", label: "Operatori, offerte e prezzi", icon: Smartphone },
      { to: "console-sim", label: "Console SIM", icon: Receipt },
      { to: "statistiche-sim", label: "Attivazioni e migrazioni", icon: BarChart3 },
      { to: "vendi-esim", label: "Catalogo eSIM", icon: Signal },
    ] },
    { label: "Servizi & Fornitori", items: [
      { to: "ricariche-estero", label: "Ricarica operatori", icon: PhoneCall },
      { to: "catalogo-giftcard", label: "Catalogo Gift Card", icon: Gift },
      { to: "offerte-rete-servizi", label: "Offerte servizi visibili", icon: Package },
      { to: "pec", label: "PEC", icon: Mail },
      { to: "raccomandata", label: "Raccomandata A/R", icon: PackageCheck },
      { to: "bollettini", label: "Bollettini / PagoPA", icon: ReceiptText },
      { to: "valutazioni", label: "Valutazioni Immobiliari", icon: Home },
      { to: "richieste-internet", label: "Richieste Internet", icon: Wifi },
      { to: "prezzi-servizi", label: "Prezzi Servizi", icon: BadgePercent },
    ] },
    { label: "Spedizioni", items: [
      { to: "console-spedizioni", label: "Console Spedizioni", icon: PackageCheck },
      { to: "config-spedizioni", label: "Config Spedizioni", icon: Truck },
      { to: "servizi-spedizioni", label: "Servizi & ricarichi spedizioni", icon: Truck },
    ] },
    { label: "Provvigioni & Pagamenti", items: [
      { to: "provvigioni-energia", label: "Ripartizione provvigioni di rete", icon: BadgePercent },
      { to: "report-ordini", label: "Report Ordini & Provvigioni", icon: Receipt },
      { to: "marginalita", label: "Marginalità ordini", icon: BarChart3 },
      { to: "provvigioni-agenti", label: "Provvigioni Agenti", icon: BadgePercent },
      { to: "referral-payout", label: "Payout Referral", icon: HandCoins },
      { to: "wallet-admin", label: "Wallet", icon: Wallet },
      { to: "audit-crediti", label: "Log & Backup Crediti", icon: History },
      { to: "pagamenti", label: "Log Pagamenti", icon: CreditCard },
    ] },
    { label: "Supporto & Sistema", items: [
      { to: "identita-kyc", label: "Identità (KYC)", icon: ShieldCheck },
      { to: "documenti-scadenza", label: "Documenti in scadenza", icon: AlertTriangle },
      { to: "assistenza", label: "Ticket Assistenza", icon: LifeBuoy },
      { to: "guida", label: "Guida & FAQ", icon: BookOpen },
      { to: "log-accessi", label: "Log accessi", icon: LogIn },
      { to: "email-inviate", label: "Email inviate", icon: Mail },
      { to: "log-errori", label: "Log Errori", icon: AlertTriangle },
    ] },
    { label: "Gestione", items: [
      { to: "pannello-servizi", label: "Pannello Servizi", icon: Boxes },
      { to: "ordini", label: "Ordini & Ricariche", icon: Receipt },
      { to: "fatture", label: "Fatture da emettere", icon: FileText },
      { to: "cestino-energia", label: "Cestino", icon: Trash2 },
    ] },
    { label: "Impostazioni", items: [
      { to: "registrazioni-pubbliche", label: "Registrazioni pubbliche", icon: Users },
      { to: "integrazioni", label: "Integrazioni", icon: Plug },
      { to: "contratto-piattaforma", label: "Contratto & Dati piattaforma", icon: FileText },
      { to: "specifiche", label: "Specifiche & revisioni", icon: FileText },
      { to: "kit-marketing", label: "Kit Marketing", icon: Palette },
      { to: "manutenzione", label: "Manutenzione", icon: Wrench },
      { to: "account", label: "Il mio account", icon: UserCog },
      { to: "sicurezza", label: "Sicurezza", icon: ShieldCheck },
    ] },
  ],
  dealer: [
    { label: null, items: [{ to: "dashboard", label: "Dashboard", icon: LayoutDashboard }, { to: "compliance", label: "Compliance", icon: ShieldCheck }] },
    { label: "Mobile", items: [
      { to: "vendi-sim", label: "Attiva e gestisci SIM", icon: ShoppingCart, flag: "italiamobile" },
      { to: "ordina-sim", label: "Ordina SIM", icon: Receipt, flag: "italiamobile" },
      { to: "console-sim", label: "Console SIM", icon: Receipt, anyFlags: ["italiamobile", "esim"] },
      { to: "statistiche-sim", label: "Attivazioni e migrazioni", icon: BarChart3, anyFlags: ["italiamobile", "esim"] },
      { to: "ricariche-estero", label: "Ricarica operatori", icon: PhoneCall, flag: "ricariche_estero" },
      { to: "mobile/italiamobile", label: "Italia Mobile", icon: Smartphone, flag: "italiamobile", op: "italiamobile", section: "I tuoi operatori" },
      { to: "mobile/emobile24", label: "E-Mobile24", icon: Smartphone, flag: "italiamobile", op: "emobile24", section: "I tuoi operatori" },
      { to: "mobile/unomobile", label: "Uno Mobile", icon: Smartphone, flag: "italiamobile", op: "unomobile", section: "I tuoi operatori" },
      { to: "esim-simamma", label: "SiMamma eSIM", icon: Signal, flag: "esim", op: "simamma", section: "I tuoi operatori" },
    ] },
    { label: "Internet", items: [
      { to: "internet", label: "Internet & Connettività", icon: Wifi, flag: "internet" },
      { to: "richieste-internet", label: "Le mie richieste Internet", icon: Wifi, flag: "internet" },
    ] },
    { label: "Servizi pubblici", items: [
      { to: "catalogo-giftcard", label: "Catalogo Gift Card", icon: Gift, flag: "giftcard_catalog" },
      { to: "bollettini", label: "Bollettini / PagoPA", icon: ReceiptText, flag: "bollettini" },
      { to: "valutazioni", label: "Valutazioni Immobiliari", icon: Home, flag: "valutazioni" },
      { to: "pec", label: "PEC", icon: Mail, flag: "pec" },
      { to: "raccomandata", label: "Raccomandata A/R", icon: PackageCheck, flag: "raccomandata" },
    ] },
    { label: "Luce & Gas", items: [
      { to: "energia", label: "Nuova pratica luce/gas", icon: Flame, flag: "energia" },
      { to: "richieste-energia", label: "Le mie pratiche luce/gas", icon: Receipt, flag: "energia" },
    ] },
    { label: "Spedizioni", items: [
      { to: "spedizioni", label: "Prenota spedizione", icon: Truck, flag: "spedizioni" },
      { to: "spedizioni?tab=reso", label: "Spedisci un reso", icon: Undo2, flag: "spedizioni" },
      { to: "console-spedizioni", label: "Le mie spedizioni", icon: PackageCheck, flag: "spedizioni" },
      { to: "rubrica", label: "Rubrica contatti", icon: BookUser, flag: "spedizioni" },
    ] },
    { label: "Acquisti", items: [
      { to: "negozio-hardware", label: "Negozio hardware", icon: Package, flag: "negozio_hardware" },
      { to: "kit-accessori", label: "Kit accessori", icon: Package },
    ] },
    { label: "Gestione", items: [
      { to: "mie-provvigioni-energia", label: "Provvigioni e guadagni", icon: BadgePercent },
      { to: "provvigioni-offerte-energia", label: "Listino", icon: Coins },
      { to: "account", label: "Il mio account", icon: UserCog },
      { to: "sicurezza", label: "Sicurezza", icon: ShieldCheck },
      { to: "vetrina", label: "La mia vetrina", icon: Store },
      { to: "pagamenti", label: "Pagamenti", icon: CreditCard },
      { to: "wallet", label: "Wallet / Plafond servizi", icon: Wallet },
      { to: "storico-wallet", label: "Storico wallet", icon: Receipt },
      { to: "fatture", label: "Fatture e movimenti", icon: FileText },
      { to: "clienti", label: "Clienti", icon: Users },
      { to: "ordini", label: "Ordini", icon: Receipt },
      { to: "documenti", label: "Documenti / Visura", icon: FileText },
      { to: "cestino-energia", label: "Cestino", icon: Trash2 },
      { to: "kit-marketing", label: "Kit Marketing", icon: Palette },
      { to: "assistenza", label: "Assistenza", icon: LifeBuoy },
      { to: "guida", label: "Guida & FAQ", icon: BookOpen },
    ] },
    { label: "Dealer", items: [
      { to: "master-dealer", label: "I miei dealer", icon: Network, masterOnly: true },
    ] },
  ],
  customer: [
    { label: null, items: [
      { to: "dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "servizi", label: "Servizi", icon: ShoppingBag },
      { to: "esim-simamma", label: "Acquista eSIM SiMamma", icon: Signal, flag: "esim" },
      { to: "miei-acquisti", label: "I miei acquisti", icon: Receipt },
      { to: "pagamenti", label: "Pagamenti", icon: CreditCard },
      { to: "catalogo", label: "Catalogo", icon: ShoppingBag },
      { to: "assistenza", label: "Assistenza", icon: LifeBuoy },
      { to: "guida", label: "Guida & FAQ", icon: BookOpen },
      { to: "account", label: "Il mio account", icon: UserCog },
      { to: "sicurezza", label: "Sicurezza", icon: ShieldCheck },
    ] },
    { label: "Spedizioni", items: [
      { to: "spedizioni", label: "Prenota spedizione", icon: Truck },
      { to: "console-spedizioni", label: "Le mie spedizioni", icon: PackageCheck },
      { to: "rubrica", label: "Rubrica contatti", icon: BookUser },
    ] },
  ],
  support: [
    { label: null, items: [
      { to: "assistenza", label: "Ticket Assistenza", icon: LifeBuoy },
      { to: "log-errori", label: "Log Errori", icon: AlertTriangle },
      { to: "guida", label: "Guida & FAQ", icon: BookOpen },
      { to: "account", label: "Il mio account", icon: UserCog },
      { to: "sicurezza", label: "Sicurezza", icon: ShieldCheck },
    ] },
  ],
  agent: [
    { label: "La mia rete", items: [
      { to: "agent-dealers", label: "I miei dealer", icon: Users },
      { to: "statistiche-sim", label: "Attivazioni e migrazioni SIM", icon: BarChart3 },
      { to: "agent-invites", label: "Inviti", icon: Mail },
      { to: "richieste-energia", label: "Pratiche luce/gas", icon: Receipt },
      { to: "agent-provvigioni", label: "Le mie provvigioni", icon: HandCoins },
      { to: "mie-provvigioni-energia", label: "Provvigioni maturate", icon: BadgePercent },
      { to: "provvigioni-offerte-energia", label: "Listino", icon: Coins },
    ] },
    { label: "Compra e vendi", items: [
      { to: "ricariche-estero", label: "Ricarica operatori", icon: PhoneCall, flag: "ricariche_estero" },
      { to: "vendi-sim", label: "Attiva e gestisci SIM", icon: ShoppingCart, flag: "italiamobile" },
      { to: "ordina-sim", label: "Ordina SIM", icon: Receipt, flag: "italiamobile" },
      { to: "catalogo-giftcard", label: "Catalogo Gift Card", icon: Gift, flag: "giftcard_catalog" },
      { to: "negozio-hardware", label: "Negozio hardware", icon: Package, flag: "negozio_hardware" },
      { to: "kit-accessori", label: "Kit accessori", icon: Package, flag: "kit_accessori" },
      { to: "pec", label: "PEC", icon: Mail, flag: "pec" },
      { to: "energia", label: "Nuova pratica luce/gas", icon: Flame },
    ] },
    { label: "Spedizioni", items: [
      { to: "spedizioni", label: "Prenota spedizione", icon: Truck, flag: "spedizioni" },
      { to: "console-spedizioni", label: "Spedizioni mie e della rete", icon: PackageCheck, flag: "spedizioni" },
    ] },
    { label: "Pagamenti", items: [
      { to: "wallet", label: "Wallet / Plafond (carta o bonifico)", icon: Wallet },
      { to: "storico-wallet", label: "Storico wallet", icon: Receipt },
    ] },
    { label: "Gestione", items: [
      { to: "cestino-energia", label: "Cestino", icon: Trash2 },
    ] },
    { label: "Account", items: [
      { to: "assistenza", label: "Ticket Assistenza", icon: LifeBuoy },
      { to: "guida", label: "Guida & FAQ", icon: BookOpen },
      { to: "account", label: "Il mio account", icon: UserCog },
      { to: "sicurezza", label: "Sicurezza", icon: ShieldCheck },
    ] },
  ],
};

export default function DashboardLayout() {
  const { user, logout, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const historyService = new URLSearchParams(location.search).get("storico");
  const [servicePagePath, setServicePagePath] = useState(historyService ? null : location.pathname);
  useEffect(() => { if (!historyService) setServicePagePath(location.pathname); }, [historyService, location.pathname]);
  useEffect(() => { if (historyService === "spedizioni") { navigate("/app/console-spedizioni", { replace: true }); } }, [historyService, navigate]);
  const groups = NAV[user?.role] || NAV.customer;
  const roleLabel = { admin: "Amministratore", dealer: "Dealer", agent: "Agenzia", customer: "Cliente" }[user?.role];

  const stripQuery = (t) => (t || "").split("?")[0];
  const currentPath = location.pathname.replace(/^\/app\/?/, "").split("?")[0];
  const [compliance, setCompliance] = useState(null);
  const [complianceOwner, setComplianceOwner] = useState(null);
  const [complianceError, setComplianceError] = useState(false);
  const [complianceAttempt, setComplianceAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setCompliance(null); setComplianceError(false); setComplianceOwner(user?.id);
    if (user?.role === "dealer") api.get("/compliance/status").then((r) => {
      if (!active) return;
      if (typeof r.data?.signed !== "boolean" && r.data?.exempt !== true) { setComplianceError(true); return; }
      setCompliance(r.data);
    }).catch(() => { if (active) setComplianceError(true); });
    return () => { active = false; };
  }, [user, complianceAttempt]);
  const currentCompliance = complianceOwner === user?.id ? compliance : null;
  const historyWaiting = !!historyService && user?.role === "dealer" && currentCompliance === null;
  const complianceExempt = currentCompliance?.exempt === true || (currentCompliance == null && user?.compliance_exempt === true);
  const complianceBlocked = user?.role === "dealer" && !complianceExempt && currentCompliance?.signed === false;
  const currentTab = new URLSearchParams(location.search).get("tab") || "";
  const [openGroup, setOpenGroup] = useState(null);
  useEffect(() => {
    const active = groups.find((g) => g.label && g.items.some((it) => stripQuery(it.to) === currentPath));
    setOpenGroup(active?.label || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath, user?.role, user?.id]);
  const toggleGroup = (label) => setOpenGroup((previous) => previous === label ? null : label);

  const [flags, setFlags] = useState({});
  const mobileOps = useOperators();
  useEffect(() => { api.get("/feature-flags").then((r) => setFlags(r.data.flags || {})).catch(() => {}); }, []);

  const onLogout = async () => { await logout(); navigate("/"); };

  const onStopImp = async () => {
    try { await stopImpersonation(); toast.success("Sessione admin ripristinata"); navigate("/app/dashboard"); }
    catch { toast.error("Impossibile tornare ad admin"); }
  };

  const resendVerify = async () => {
    try { await api.post("/auth/resend-verification"); toast.success("Email di conferma inviata"); }
    catch { toast.error("Errore, riprova più tardi"); }
  };

  const linkClass = (active) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
      active ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
    }`;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [compactLayout, setCompactLayout] = useState(() => window.matchMedia("(max-width: 1023px)").matches);
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.search, user?.id]);
  useEffect(() => {
    const mobile = window.matchMedia("(max-width: 1023px)");
    const update = () => { setCompactLayout(mobile.matches); if (!mobile.matches) setMobileOpen(false); };
    mobile.addEventListener("change", update);
    return () => { mobile.removeEventListener("change", update); };
  }, []);
  const activeOps = (mobileOps || []).filter((o) => o.active).map((o) => o.key);
  const navigationProps = { groups, user, flags, activeOps, complianceExempt, currentPath, currentTab, openGroup, toggleGroup };
  const blockedItem = user?.role !== "admin" ? groups.flatMap((g) => g.items).find((i) => i.to === currentPath && i.flag && flags[i.flag] === false) : null;
  return (
    <div className="min-h-screen flex bg-background">
      <GreenStandby />
      <aside className="workspace-sidebar w-64 shrink-0 border-r border-border bg-card/60 backdrop-blur-md hidden md:flex flex-col fixed h-screen">
        <div className="px-5 py-4 border-b border-border">
          <div className="flex items-center justify-between">
            <Logo size={22} />
            <NotificationsBell />
          </div>
          <p className="text-xs text-muted-foreground mt-2">{roleLabel}</p>
        </div>
        <WorkspaceNavigation {...navigationProps} />
        <div className="p-3 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <p className="text-sm font-medium break-words" data-testid="workspace-account-name">{user?.name}</p>
            <p className="text-xs text-muted-foreground break-all" data-testid="workspace-account-email">{user?.email}</p>
            <UplineLine user={user} />
          </div>
          <Button variant="outline" className="w-full justify-start gap-2" onClick={onLogout} data-testid="logout-button">
            <LogOut className="w-4 h-4" /> Esci
          </Button>
        </div>
      </aside>

      <div className="workspace-body flex-1 md:ml-64 min-w-0">
        <header className="workspace-header sticky top-0 z-40 border-b border-border bg-background/95 backdrop-blur" data-testid="workspace-header">
          <div className="md:hidden flex items-center justify-between gap-1 px-3 py-2 border-b border-border" data-testid="workspace-mobile-toolbar">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild><Button size="icon" variant="ghost" aria-label="Apri menu principale" data-testid="mobile-menu-toggle"><MobileMenuIcon className="w-5 h-5" /></Button></SheetTrigger>
              <SheetContent side="left" className="mobile-navigation-panel flex flex-col p-0 w-[min(88vw,22rem)] h-[100dvh]" data-testid="mobile-navigation-panel">
                <SheetHeader className="px-5 pt-5 pb-3 border-b border-border text-left"><SheetTitle data-testid="mobile-menu-title">Menu Markoo Place</SheetTitle><SheetDescription className="text-xs min-w-0 whitespace-normal [overflow-wrap:anywhere]" data-testid="mobile-menu-account">{user?.company_name || user?.name}</SheetDescription><UplineLine user={user} prefix="mobile-" /></SheetHeader>
                {user?.role === "dealer" && user?.is_master && <div className="px-3 pt-3" data-testid="mobile-network-slot"><MasterNetworkMenu prefix="mobile-" full onNavigate={() => setMobileOpen(false)} /></div>}
                <WorkspaceNavigation {...navigationProps} mobile onSelect={() => setMobileOpen(false)} />
                <div className="p-3 border-t border-border space-y-2"><InstallAppButton onSelect={() => setMobileOpen(false)} /><Button variant="outline" className="w-full justify-start gap-2" onClick={onLogout} data-testid="logout-button-mobile"><LogOut className="w-4 h-4" />Esci</Button></div>
              </SheetContent>
            </Sheet>
            <Logo size={17} />
            <div className="flex items-center gap-0">
              <NotificationsBell prefix="mobile-" />
              <NavLink to="/app/account?tab=account" className="p-2 rounded-md hover:bg-secondary" aria-label="La mia anagrafica" data-testid="mobile-my-profile"><UserCog className="w-4 h-4" /></NavLink>
              {compactLayout && <div data-testid="mobile-display-settings"><DisplayControls /></div>}
            </div>
          </div>
          <div className="workspace-tools-row flex flex-wrap xl:flex-nowrap items-center justify-end min-w-0">
            {!compactLayout && <div className="hidden md:flex flex-1 min-w-[240px] px-4" data-testid="header-search-slot"><GlobalSearch /></div>}
            {!compactLayout && user?.role === "dealer" && user?.is_master && <div className="hidden md:flex mr-2 relative z-10">< MasterNetworkMenu /></div>}
            {(user?.role === "dealer" || user?.role === "agent") && <div className="w-full md:w-auto md:shrink-0 min-w-0 flex md:justify-end" data-testid="header-balance-row"><DealerBalanceBar key={user.id} /></div>}
            {user?.role === "admin" && !user?.impersonating && <div className="hidden md:flex ml-auto items-center gap-1 px-2" data-testid="admin-header-menus">{NAV.admin.filter((g) => ["Rete", "Impostazioni"].includes(g.label)).map((g) => <DropdownMenu key={g.label}>
              <DropdownMenuTrigger asChild><button type="button" className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-sm hover:bg-secondary transition-colors" data-testid={`admin-header-${g.label.toLowerCase()}`}>{g.label === "Rete" ? <Network className="w-4 h-4" /> : <Settings className="w-4 h-4" />}{g.label}<ChevronDown className="w-3 h-3" /></button></DropdownMenuTrigger>
              <DropdownMenuContent align="end">{g.items.map((it) => <DropdownMenuItem key={it.to} onSelect={() => navigate(`/app/${it.to}`)} className="gap-2" data-testid={`admin-header-item-${it.to.replace(/[^a-z0-9]+/gi, "-")}`}><it.icon className="w-4 h-4" />{it.label}</DropdownMenuItem>)}</DropdownMenuContent>
            </DropdownMenu>)}</div>}
            {!compactLayout && <div className="workspace-display-row shrink-0 px-4 lg:pr-6 py-1" style={user?.role === "admin" && !user?.impersonating ? { marginInlineStart: 0 } : undefined}><DisplayControls /></div>}
          </div>

        {user?.impersonating && <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 lg:px-8 py-2 bg-primary text-primary-foreground text-sm" title={`Sessione admin: ${user.impersonator?.email}. Scade dopo ${user.imp_idle_minutes || 20} minuti di inattività.`} data-testid="impersonation-banner"><UserCog className="w-4 h-4 shrink-0" /><span className="flex-1 min-w-0 basis-48 leading-snug [overflow-wrap:anywhere]" data-testid="impersonation-identity">Stai impersonando <strong>{user.name}</strong><span className="text-xs block sm:inline sm:ml-2 min-w-0 [overflow-wrap:anywhere]">{user.email} · {roleLabel}</span><span className="sr-only">. Sessione admin: {user.impersonator?.email}. Scade dopo {user.imp_idle_minutes || 20} minuti di inattività.</span></span><Button size="sm" variant="secondary" className="shrink-0" onClick={onStopImp} data-testid="stop-impersonation-button">Torna ad Admin</Button></div>}
        </header>
        <InstallAppBanner />
        {user?.role === "customer" && <nav aria-label="Acquisti eSIM" className="md:hidden flex flex-wrap gap-2 px-4 py-3 border-b border-border" data-testid="customer-mobile-esim-nav">
          {flags.esim !== false && <NavLink to="/app/esim-simamma" className={linkClass(currentPath === "esim-simamma")} data-testid="mobile-buy-esim"><Signal className="w-4 h-4" /> Acquista eSIM SiMamma</NavLink>}
          <NavLink to="/app/miei-acquisti" className={linkClass(currentPath === "miei-acquisti")} data-testid="mobile-my-purchases"><Receipt className="w-4 h-4" /> I miei acquisti</NavLink>
        </nav>}
        <ImpersonationGuard />
        <main className={`app-page-content w-full min-w-0 px-4 sm:px-6 lg:px-8 py-5 mx-auto ${historyService ? "max-w-none" : "max-w-screen-2xl"}`} data-testid="workspace-main">
          {user && user.email_verified === false && (
            <div className="mb-5 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="verify-email-banner">
              <Mail className="w-4 h-4 text-amber-500 shrink-0" />
              <span>Conferma il tuo indirizzo email per sbloccare tutte le funzioni. Controlla la tua casella di posta.</span>
              <Button size="sm" variant="outline" className="ml-auto" onClick={resendVerify} data-testid="resend-verify-button">Reinvia email</Button>
            </div>
          )}
          <ComplianceOrderPrompt />
          {user?.role === "dealer" && <AccountStatusBanners compliance={currentCompliance} />}
          {complianceBlocked && currentPath === "dashboard" && (
            <div className="mb-5 rounded-xl border border-primary/40 bg-primary/10 px-4 py-3 flex flex-wrap items-center gap-3 text-sm" data-testid="compliance-banner">
              <ShieldCheck className="w-4 h-4 text-primary shrink-0" />
              <span data-testid="compliance-browse-notice"><strong>Modalità consultazione.</strong> Puoi esplorare la piattaforma. Completa la compliance prima del primo ordine o delle modifiche operative.</span>
              <Button size="sm" className="ml-auto" onClick={() => navigate("/app/compliance")} data-testid="compliance-banner-cta">Compliance</Button>
            </div>
          )}
          {historyWaiting ? (
            <section className="rounded-xl border border-border bg-card p-6 space-y-4" data-testid="history-access-pending">
              <p role={complianceError ? "alert" : "status"} data-testid="history-access-message">{complianceError ? "Impossibile aprire lo storico in questo momento. Riprova." : "Caricamento storico…"}</p>
              <div className="flex flex-wrap gap-2">{complianceError && <Button onClick={() => setComplianceAttempt((value) => value + 1)} data-testid="history-access-retry">Riprova</Button>}<Button variant="outline" onClick={() => { const next = new URLSearchParams(location.search); next.delete("storico"); next.delete("richiesta"); navigate({ pathname: location.pathname, search: next.toString() }); }} data-testid="history-access-back">Torna al servizio</Button></div>
            </section>
          ) : (
            <>
              <ForcePasswordChange />
              <div hidden={!!historyService} data-testid="service-page-content">{(!historyService || servicePagePath === location.pathname) && (blockedItem ? <div className="max-w-lg mx-auto mt-16 rounded-2xl border border-border bg-card p-8 text-center space-y-3" data-testid="service-disabled"><Power className="w-8 h-8 mx-auto text-muted-foreground" /><h1 className="font-head text-2xl font-bold">Servizio non disponibile</h1><p className="text-sm text-muted-foreground" data-testid="service-disabled-name">{blockedItem.label} al momento non è attivo per il tuo account. Riprova più tardi o contatta l'assistenza.</p><Button variant="outline" onClick={() => navigate("/app/dashboard")} data-testid="service-disabled-home">Torna alla dashboard</Button></div> : <Outlet />)}</div>
              {historyService && <ServiceHistoryPage key={`${user.id}-${user.role}-${historyService}`} service={historyService} />}
            </>
          )}
        </main>
        <footer className={`workspace-footer px-4 sm:px-6 lg:px-8 py-4 border-t border-border mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3 text-xs leading-relaxed text-muted-foreground mx-auto w-full ${historyService ? "max-w-none" : "max-w-screen-2xl"}`} data-testid="workspace-footer">
          <CompanyIdentity testId="workspace-company-identity" />
          <NavLink to="/app/novita" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors" data-testid="footer-changelog-link">
            <Sparkles className="w-3.5 h-3.5" /> Novità & Changelog
          </NavLink>
        </footer>
      </div>
    </div>
  );
}
